/**
 * StudioTopBar - the studio's own (de-chromed) top bar. A back-breadcrumb to the
 * category, a template picker (a category can have several), the name + status,
 * the preview-mode toggle, a live autosave indicator, and the Save / Publish /
 * Duplicate actions. No app nav - the studio is its own focused surface.
 */

import { Button, DropdownMenu, Pill, SegmentedControl, Select, SelectItem } from '@protoss/ui';
import { ArrowLeftIcon } from '@protoss/ui/icons';
import { Link } from 'react-router-dom';
import type { PreviewMode } from '../../../../components/doc';
import type { SaveStatus } from './useSowStudio';

export interface StudioTopBarProps {
  backHref: string;
  backLabel: string;
  templates: Array<{ id: number; label: string }>;
  selectedTemplateId: number;
  onSelectTemplate: (id: number) => void;
  label: string;
  status: string;
  isDefault: boolean;
  canEdit: boolean;
  isDirty: boolean;
  saveStatus: SaveStatus;
  publishing: boolean;
  fidelityMode: 'design' | 'proof';
  onFidelityModeChange: (m: 'design' | 'proof') => void;
  previewMode: PreviewMode;
  onPreviewModeChange: (m: PreviewMode) => void;
  onSave: () => void;
  onPublish: () => void;
  onDuplicate: () => void;
}

function autosaveLabel(canEdit: boolean, status: SaveStatus, isDirty: boolean): string {
  if (!canEdit) return 'Read-only';
  if (status === 'saving') return 'Saving…';
  if (status === 'error') return 'Save failed';
  if (isDirty) return 'Unsaved changes';
  return 'Saved';
}

export function StudioTopBar({
  backHref,
  backLabel,
  templates,
  selectedTemplateId,
  onSelectTemplate,
  label,
  status,
  isDefault,
  canEdit,
  isDirty,
  saveStatus,
  publishing,
  fidelityMode,
  onFidelityModeChange,
  previewMode,
  onPreviewModeChange,
  onSave,
  onPublish,
  onDuplicate,
}: StudioTopBarProps) {
  const live = status === 'published' && isDefault;
  return (
    <header className="sow-studio-topbar">
      <div className="sow-studio-topbar__left">
        <Link className="sow-studio-topbar__back" to={backHref}>
          <ArrowLeftIcon />
          <span>{backLabel}</span>
        </Link>
        <span className="sow-studio-topbar__sep" aria-hidden>
          /
        </span>
        {templates.length > 1 ? (
          <Select
            value={String(selectedTemplateId)}
            onValueChange={(v) => onSelectTemplate(Number(v))}
            size="sm"
          >
            {templates.map((t) => (
              <SelectItem key={t.id} value={String(t.id)}>
                {t.label}
              </SelectItem>
            ))}
          </Select>
        ) : (
          <span className="sow-studio-topbar__name">{label}</span>
        )}
        {live ? (
          <Pill variant="success">Active</Pill>
        ) : (
          <Pill variant={status === 'published' ? 'primary' : 'muted'}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Pill>
        )}
        {!canEdit ? <Pill variant="ghost">System - duplicate to edit</Pill> : null}
      </div>

      <div className="sow-studio-topbar__right">
        <SegmentedControl<'design' | 'proof'>
          ariaLabel="Fidelity"
          value={fidelityMode}
          onChange={onFidelityModeChange}
          options={[
            { value: 'design', label: 'Design' },
            { value: 'proof', label: 'Proof' },
          ]}
        />
        {fidelityMode === 'design' ? (
          <SegmentedControl<PreviewMode>
            ariaLabel="Preview mode"
            value={previewMode}
            onChange={onPreviewModeChange}
            options={[
              { value: 'sample', label: 'Sample' },
              { value: 'placeholder', label: 'Tokens' },
            ]}
          />
        ) : null}
        <span
          className={`sow-studio-topbar__status sow-studio-topbar__status--${
            saveStatus === 'error' ? 'error' : isDirty && canEdit ? 'dirty' : 'saved'
          }`}
        >
          {autosaveLabel(canEdit, saveStatus, isDirty)}
        </span>
        {canEdit ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onSave}
              disabled={!isDirty || saveStatus === 'saving'}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onPublish}
              disabled={publishing}
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        ) : null}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button type="button" variant="ghost" size="sm">
              ⋯
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end" className="dropdown-content">
            <DropdownMenu.Item className="dropdown-item" onSelect={onDuplicate}>
              Duplicate template
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
